// Funnel B — operator management surface.
//
// Three panels in one view:
//   · Agents     (add agent, toggle available)
//   · Licenses   (add license to agent, toggle active)
//   · Ingest keys (issue new key with one-time-secret modal, toggle enabled)
//
// All writes use the JWT-scoped supabase client via /api/funnel-b/*.

import React from 'react';
import { readPersistedJwtDirect } from '../lib/authedFetch';
import {
  Loader2, Plus, Power, KeyRound, Users, BadgeCheck,
  Copy, AlertTriangle, RefreshCcw, CheckCircle2,
} from 'lucide-react';
import { supabase } from '../lib/supabase';

async function apiFetch(path, opts = {}) {
  const jwt = readPersistedJwtDirect();
  const res = await fetch(path, {
    method: opts.method || 'GET',
    headers: {
      'Content-Type': 'application/json',
      ...(jwt ? { Authorization: `Bearer ${jwt}` } : {}),
    },
    body: opts.body ? JSON.stringify(opts.body) : undefined,
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body?.error || `HTTP ${res.status}`);
  return body;
}

const NICHE_OPTIONS = ['all', 'life', 'medicare', 'aca', 'pc'];

// ─── Agents panel ────────────────────────────────────────────────
function AgentsPanel({ agents, onChange, showToast }) {
  const [form, setForm] = React.useState({ display_name: '', phone_e164: '', email: '', max_concurrent: 1 });
  const [busy, setBusy] = React.useState(false);

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await apiFetch('/api/funnel-b/agents', { method: 'POST', body: form });
      showToast('Agent added', 'success');
      setForm({ display_name: '', phone_e164: '', email: '', max_concurrent: 1 });
      onChange();
    } catch (err) {
      showToast(`Add failed: ${err.message}`, 'error');
    } finally { setBusy(false); }
  };

  const toggleAvailable = async (a) => {
    try {
      await apiFetch(`/api/funnel-b/agents?id=${a.id}`, { method: 'PATCH', body: { is_available: !a.is_available } });
      onChange();
    } catch (err) {
      showToast(`Toggle failed: ${err.message}`, 'error');
    }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4">
      <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-3 flex items-center gap-1">
        <Users size={11}/> Agents ({agents.length})
      </div>

      <form onSubmit={create} className="grid grid-cols-1 sm:grid-cols-5 gap-2 items-end mb-4 pb-4 border-b border-slate-100">
        <label className="sm:col-span-1 text-xs">
          <span className="block text-[10px] text-slate-500 mb-0.5">Display name</span>
          <input required value={form.display_name} onChange={e => setForm({ ...form, display_name: e.target.value })}
                 className="w-full border border-slate-300 rounded px-2 py-1 text-xs" placeholder="Jane Doe"/>
        </label>
        <label className="sm:col-span-1 text-xs">
          <span className="block text-[10px] text-slate-500 mb-0.5">Phone (E.164)</span>
          <input required value={form.phone_e164} onChange={e => setForm({ ...form, phone_e164: e.target.value })}
                 className="w-full border border-slate-300 rounded px-2 py-1 text-xs font-mono" placeholder="+15125550100"/>
        </label>
        <label className="sm:col-span-1 text-xs">
          <span className="block text-[10px] text-slate-500 mb-0.5">Email (optional)</span>
          <input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })}
                 className="w-full border border-slate-300 rounded px-2 py-1 text-xs" placeholder="jane@agency.com"/>
        </label>
        <label className="sm:col-span-1 text-xs">
          <span className="block text-[10px] text-slate-500 mb-0.5">Max concurrent</span>
          <input type="number" min="1" max="10" value={form.max_concurrent}
                 onChange={e => setForm({ ...form, max_concurrent: Number(e.target.value) || 1 })}
                 className="w-full border border-slate-300 rounded px-2 py-1 text-xs"/>
        </label>
        <button type="submit" disabled={busy} className="sm:col-span-1 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-40 text-white text-xs font-bold rounded px-3 py-1.5 inline-flex items-center justify-center gap-1">
          {busy ? <Loader2 size={11} className="animate-spin"/> : <Plus size={11}/>} Add agent
        </button>
      </form>

      {agents.length === 0 ? (
        <div className="text-xs text-slate-500 italic">No agents yet. Add one above.</div>
      ) : (
        <table className="w-full text-xs">
          <thead className="bg-slate-50 text-slate-500">
            <tr>
              <th className="text-left p-1.5">Name</th>
              <th className="text-left p-1.5">Phone</th>
              <th className="text-left p-1.5">Email</th>
              <th className="text-right p-1.5">Load / Max</th>
              <th className="text-right p-1.5">Available</th>
            </tr>
          </thead>
          <tbody>
            {agents.map(a => (
              <tr key={a.id} className="border-t border-slate-100">
                <td className="p-1.5 font-bold">{a.display_name}</td>
                <td className="p-1.5 font-mono">{a.phone_e164}</td>
                <td className="p-1.5 text-slate-600">{a.email || '—'}</td>
                <td className="p-1.5 text-right tabular-nums">{a.current_load || 0} / {a.max_concurrent}</td>
                <td className="p-1.5 text-right">
                  <button onClick={() => toggleAvailable(a)} className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded inline-flex items-center gap-1 ${a.is_available ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-200 text-slate-600'}`}>
                    <Power size={9}/> {a.is_available ? 'On' : 'Off'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

// ─── Licenses panel ──────────────────────────────────────────────
function LicensesPanel({ agents, licenses, onChange, showToast }) {
  const [form, setForm] = React.useState({ agent_id: '', state_code: '', niche: 'all', license_number: '' });
  const [busy, setBusy] = React.useState(false);

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await apiFetch('/api/funnel-b/licenses', { method: 'POST', body: form });
      showToast('License added', 'success');
      setForm({ agent_id: '', state_code: '', niche: 'all', license_number: '' });
      onChange();
    } catch (err) {
      showToast(`Add failed: ${err.message}`, 'error');
    } finally { setBusy(false); }
  };

  const toggleActive = async (l) => {
    try {
      await apiFetch(`/api/funnel-b/licenses?id=${l.id}`, { method: 'PATCH', body: { active: !l.active } });
      onChange();
    } catch (err) {
      showToast(`Toggle failed: ${err.message}`, 'error');
    }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4">
      <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-3 flex items-center gap-1">
        <BadgeCheck size={11}/> Licenses ({licenses.length})
      </div>

      <form onSubmit={create} className="grid grid-cols-1 sm:grid-cols-5 gap-2 items-end mb-4 pb-4 border-b border-slate-100">
        <label className="sm:col-span-2 text-xs">
          <span className="block text-[10px] text-slate-500 mb-0.5">Agent</span>
          <select required value={form.agent_id} onChange={e => setForm({ ...form, agent_id: e.target.value })}
                  className="w-full border border-slate-300 rounded px-2 py-1 text-xs">
            <option value="">— select agent —</option>
            {agents.map(a => <option key={a.id} value={a.id}>{a.display_name} ({a.phone_e164})</option>)}
          </select>
        </label>
        <label className="sm:col-span-1 text-xs">
          <span className="block text-[10px] text-slate-500 mb-0.5">State (2-letter)</span>
          <input required maxLength={2} value={form.state_code}
                 onChange={e => setForm({ ...form, state_code: e.target.value.toUpperCase() })}
                 className="w-full border border-slate-300 rounded px-2 py-1 text-xs uppercase" placeholder="TX"/>
        </label>
        <label className="sm:col-span-1 text-xs">
          <span className="block text-[10px] text-slate-500 mb-0.5">Niche</span>
          <select value={form.niche} onChange={e => setForm({ ...form, niche: e.target.value })}
                  className="w-full border border-slate-300 rounded px-2 py-1 text-xs">
            {NICHE_OPTIONS.map(n => <option key={n} value={n}>{n}</option>)}
          </select>
        </label>
        <label className="sm:col-span-1 text-xs sm:col-start-1 sm:row-start-2">
          <span className="block text-[10px] text-slate-500 mb-0.5">License # (optional)</span>
          <input value={form.license_number} onChange={e => setForm({ ...form, license_number: e.target.value })}
                 className="w-full border border-slate-300 rounded px-2 py-1 text-xs font-mono"/>
        </label>
        <button type="submit" disabled={busy || !form.agent_id || !form.state_code}
                className="sm:col-span-1 sm:col-start-5 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-40 text-white text-xs font-bold rounded px-3 py-1.5 inline-flex items-center justify-center gap-1">
          {busy ? <Loader2 size={11} className="animate-spin"/> : <Plus size={11}/>} Add license
        </button>
      </form>

      {licenses.length === 0 ? (
        <div className="text-xs text-slate-500 italic">No licenses yet. Add one above.</div>
      ) : (
        <table className="w-full text-xs">
          <thead className="bg-slate-50 text-slate-500">
            <tr>
              <th className="text-left p-1.5">Agent</th>
              <th className="text-left p-1.5">State</th>
              <th className="text-left p-1.5">Niche</th>
              <th className="text-left p-1.5">License #</th>
              <th className="text-right p-1.5">Active</th>
            </tr>
          </thead>
          <tbody>
            {licenses.map(l => (
              <tr key={l.id} className="border-t border-slate-100">
                <td className="p-1.5">{l.agent_display_name || l.agent_id.slice(0, 8)}</td>
                <td className="p-1.5 font-mono">{l.state_code}</td>
                <td className="p-1.5">{l.niche}</td>
                <td className="p-1.5 font-mono text-slate-600">{l.license_number || '—'}</td>
                <td className="p-1.5 text-right">
                  <button onClick={() => toggleActive(l)} className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded inline-flex items-center gap-1 ${l.active ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-200 text-slate-600'}`}>
                    <Power size={9}/> {l.active ? 'Active' : 'Off'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

// ─── Ingest keys panel ───────────────────────────────────────────
function SecretModal({ secret, prefix, name, onClose }) {
  const [copied, setCopied] = React.useState(false);
  const [acknowledged, setAcknowledged] = React.useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(secret);
      setCopied(true);
    } catch {
      window.prompt('Copy this secret manually (Cmd/Ctrl+C):', secret);
      setCopied(true);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl max-w-2xl w-full p-5 shadow-2xl">
        <div className="flex items-center gap-2 mb-3">
          <AlertTriangle size={18} className="text-amber-600"/>
          <h2 className="text-lg font-black">Save this secret NOW</h2>
        </div>
        <p className="text-sm text-slate-700 mb-3">
          This is the <strong>only time</strong> the full ingest secret for <code className="text-xs bg-slate-100 px-1.5 py-0.5 rounded">{name}</code> will be shown.
          Copy it into your password manager or vendor configuration. Once you close this dialog, only the prefix and last 4 will remain visible — there is no way to retrieve the secret again. (You'd have to rotate.)
        </p>

        <div className="bg-slate-50 border border-slate-200 rounded p-3 mb-3">
          <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">Ingest prefix (URL clientKey)</div>
          <div className="font-mono text-sm text-slate-900 mb-3">{prefix}</div>

          <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">Secret (HMAC signing key)</div>
          <div className="font-mono text-xs text-slate-900 break-all bg-white border border-slate-200 rounded p-2 mb-2">{secret}</div>

          <button onClick={copy} className="text-xs font-bold inline-flex items-center gap-1 bg-indigo-600 hover:bg-indigo-700 text-white rounded px-3 py-1.5">
            {copied ? <CheckCircle2 size={11}/> : <Copy size={11}/>}
            {copied ? 'Copied to clipboard' : 'Copy secret'}
          </button>
        </div>

        <label className="flex items-start gap-2 text-xs text-slate-700 mb-4">
          <input type="checkbox" checked={acknowledged} onChange={e => setAcknowledged(e.target.checked)} className="mt-0.5"/>
          <span>I have saved the secret in a secure location. I understand it will <strong>not</strong> be retrievable after I close this dialog.</span>
        </label>

        <div className="flex justify-end">
          <button onClick={onClose} disabled={!acknowledged}
                  className="bg-slate-900 hover:bg-slate-800 disabled:opacity-30 text-white text-xs font-bold rounded px-4 py-2">
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

function IngestKeysPanel({ keys, onChange, showToast }) {
  const [name, setName] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [secretModal, setSecretModal] = React.useState(null);

  const issue = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await apiFetch('/api/funnel-b/ingest-keys', { method: 'POST', body: { name } });
      setSecretModal({ secret: r.key.secret, prefix: r.key.prefix, name: r.key.name });
      setName('');
      onChange();
    } catch (err) {
      showToast(`Issue failed: ${err.message}`, 'error');
    } finally { setBusy(false); }
  };

  const toggleEnabled = async (k) => {
    try {
      await apiFetch(`/api/funnel-b/ingest-keys?id=${k.id}`, { method: 'PATCH', body: { enabled: !k.enabled } });
      onChange();
    } catch (err) {
      showToast(`Toggle failed: ${err.message}`, 'error');
    }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4">
      <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-3 flex items-center gap-1">
        <KeyRound size={11}/> Ingest keys ({keys.length})
      </div>

      <form onSubmit={issue} className="flex items-end gap-2 mb-4 pb-4 border-b border-slate-100">
        <label className="flex-1 text-xs">
          <span className="block text-[10px] text-slate-500 mb-0.5">Vendor / source name</span>
          <input required value={name} onChange={e => setName(e.target.value)}
                 className="w-full border border-slate-300 rounded px-2 py-1 text-xs" placeholder="leadhoop, smartfinancial, internal_test…"/>
        </label>
        <button type="submit" disabled={busy} className="bg-indigo-600 hover:bg-indigo-700 disabled:opacity-40 text-white text-xs font-bold rounded px-3 py-1.5 inline-flex items-center gap-1">
          {busy ? <Loader2 size={11} className="animate-spin"/> : <KeyRound size={11}/>} Issue key
        </button>
      </form>

      {keys.length === 0 ? (
        <div className="text-xs text-slate-500 italic">No keys yet. Issue one above to start receiving leads.</div>
      ) : (
        <table className="w-full text-xs">
          <thead className="bg-slate-50 text-slate-500">
            <tr>
              <th className="text-left p-1.5">Name</th>
              <th className="text-left p-1.5">Prefix</th>
              <th className="text-left p-1.5">Suffix</th>
              <th className="text-left p-1.5">Last used</th>
              <th className="text-right p-1.5">Enabled</th>
            </tr>
          </thead>
          <tbody>
            {keys.map(k => (
              <tr key={k.id} className="border-t border-slate-100">
                <td className="p-1.5 font-bold">{k.name}</td>
                <td className="p-1.5 font-mono text-slate-700">{k.prefix}</td>
                <td className="p-1.5 font-mono text-slate-400">…{k.suffix}</td>
                <td className="p-1.5 text-slate-600">{k.last_used_at ? new Date(k.last_used_at).toLocaleString() : '—'}</td>
                <td className="p-1.5 text-right">
                  <button onClick={() => toggleEnabled(k)} className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded inline-flex items-center gap-1 ${k.enabled ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-200 text-slate-600'}`}>
                    <Power size={9}/> {k.enabled ? 'On' : 'Off'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {secretModal && (
        <SecretModal {...secretModal} onClose={() => setSecretModal(null)}/>
      )}
    </div>
  );
}

// ─── Container ───────────────────────────────────────────────────
export default function FunnelBManage({ showToast = () => {} }) {
  const [agents, setAgents]       = React.useState([]);
  const [licenses, setLicenses]   = React.useState([]);
  const [keys, setKeys]           = React.useState([]);
  const [loading, setLoading]     = React.useState(true);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const [a, l, k] = await Promise.all([
        apiFetch('/api/funnel-b/agents'),
        apiFetch('/api/funnel-b/licenses'),
        apiFetch('/api/funnel-b/ingest-keys'),
      ]);
      setAgents(a.agents || []);
      setLicenses(l.licenses || []);
      setKeys(k.keys || []);
    } catch (err) {
      showToast(`Load failed: ${err.message}`, 'error');
    } finally { setLoading(false); }
  }, [showToast]);

  React.useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="text-xs text-slate-500">Manage agents, state licenses, and vendor ingest keys.</div>
        <button onClick={load} disabled={loading} className="text-xs text-slate-500 hover:text-slate-800 inline-flex items-center gap-1 disabled:opacity-30">
          <RefreshCcw size={11}/> Refresh
        </button>
      </div>
      <AgentsPanel agents={agents} onChange={load} showToast={showToast}/>
      <LicensesPanel agents={agents} licenses={licenses} onChange={load} showToast={showToast}/>
      <IngestKeysPanel keys={keys} onChange={load} showToast={showToast}/>
    </div>
  );
}
